// Canvas cursor for the physical Paint Tool: the selected brush's own contact
// layout (bristle groups or sponge pores) at the current size, from the same
// geometry the engine paints with (BrushGeometry + family parameters).

import { createBristleLayout } from '../brushes/BrushGeometry.js';
import { brushPreset } from '../brushes/BrushPresets.js';

const layouts = new Map();
const layoutOf = (preset) => {
  if (!layouts.has(preset.id)) layouts.set(preset.id, createBristleLayout(preset, 1));
  return layouts.get(preset.id);
};

// Contact layout at a medium pressure, in document pixels around the pointer.
// Returns { markup, extent } for an SVG overlay, or null for tools without bristles.
export function physicalBrushCursor(settings = {}, direction = [1, 0]) {
  const physical = settings.physical || {};
  if (String(settings.tool || '').toLowerCase() === 'knife') return null;
  const preset = brushPreset(physical.brush || 'filbert_hog');
  if (preset.tool === 'knife') return null;
  const groups = layoutOf(preset);
  const radius = Math.max(1, (Number(settings.toolSize) || 70) / 2);
  const width = radius * (preset.widthScale ?? 1);
  const pe = 0.6;
  const soft = 1 - preset.stiffness;
  const spread = 1 + preset.splay * pe * (0.5 + 0.5 * soft);
  const contactReach = 0.3 + 0.8 * Math.pow(pe, 0.7);
  const groupRadius = Math.max(0.55, ((width * 1.7) / Math.sqrt(groups.length)) * (preset.footprintScale ?? 1)) * (0.75 + 0.5 * pe);
  const angle = physical.rotationMode === 'fixed' ? 0 : Math.atan2(direction[1], direction[0]);
  const alongX = Math.cos(angle);
  const alongY = Math.sin(angle);
  const roundTaper = !(preset.layout === 'flat' || preset.layout === 'filbert' || preset.layout === 'fan' || preset.layout === 'sponge');
  const shapes = [];
  let extent = 4;
  for (const g of groups) {
    if (roundTaper && g.rest > contactReach + 0.1) continue;
    const ox = (-alongY * g.u + alongX * g.v) * width * spread;
    const oy = (alongX * g.u + alongY * g.v) * width * spread;
    const r = preset.layout === 'sponge' ? Math.max(0.6, g.poreRadius * width * 2) : groupRadius;
    extent = Math.max(extent, Math.hypot(ox, oy) + r + 2);
    shapes.push(`<circle cx="${ox.toFixed(2)}" cy="${oy.toFixed(2)}" r="${r.toFixed(2)}" />`);
  }
  const line = Math.max(0.6, Math.min(2, radius / 40));
  return {
    extent,
    markup: `<g fill="none" stroke="rgb(255,255,255)" stroke-opacity="0.75" stroke-width="${line.toFixed(2)}">${shapes.join('')}</g>`,
  };
}
